import { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'
import { ButtonLink, Divider, Flex } from '@npco/zeller-design-system'

import { rvLastSelectedDates, rvSearchPhrase } from 'graphql/reactiveVariables'
import { ROOT } from 'const/routes'
import { currencyFormatter } from 'utils/common'
import { formatWeekdayDate, formatWeekdayDateTime } from 'utils/date'
import dayjs from 'utils/dayjs'
import { translate } from 'utils/translations'
import { Deposit } from 'types/deposits'
import { DepositStatus } from 'types/gql-global-types'
import { TIME_FILTER_ENABLED_COMPONENTS } from 'types/picker'
import DepositStatusBadge from 'components/StatusBadge/DepositStatusBadge/DepositStatusBadge'
import { TooltipLabel } from 'components/TooltipLabel/TooltipLabel'

import {
  DepositDetailsContainer,
  StyledContentWrapper,
  StyledDetailsLabel,
  StyledDetailsValue,
  TooltipContent,
} from './DepositDetails.styled'

interface Props {
  deposit: Deposit
}

const DetailsField = ({
  label,
  value,
  dataTestId,
}: {
  label: ReactNode
  value: ReactNode
  dataTestId: string
}) => {
  return (
    <Flex justifyContent="space-between" mb="0.5rem" data-testid={dataTestId}>
      <StyledDetailsLabel data-testid="field-label">{label}</StyledDetailsLabel>
      <StyledDetailsValue data-testid="field-value">{value}</StyledDetailsValue>
    </Flex>
  )
}

export const DepositDetails = ({ deposit }: Props) => {
  const {
    siteName,
    timestamp,
    skippedTimestamp,
    status,
    feeAmount,
    refundsAmount,
    chargebacksAmount,
    owingAmount,
    depositAmount,
    collectedAmount,
    shortId,
  } = deposit
  const navigate = useNavigate()
  const depositDate = dayjs(timestamp)
  const isFailedSettlement = status === DepositStatus.FAILED
  const isCompletedSettlement = status === DepositStatus.COMPLETED
  return (
    <DepositDetailsContainer>
      <StyledContentWrapper>
        <DetailsField
          label={
            <TooltipLabel
              label={translate('component.deposit.dateSettled')}
              tooltipContent={
                <TooltipContent>
                  {translate('component.deposit.dateSettledTooltip')}
                </TooltipContent>
              }
            />
          }
          value={isCompletedSettlement ? formatWeekdayDateTime(timestamp) : '-'}
          dataTestId="deposit-date-settled"
        />

        {Boolean(skippedTimestamp) && (
          <DetailsField
            label={
              <TooltipLabel
                label={translate('component.deposit.dateHeld')}
                tooltipContent={
                  <TooltipContent>
                    {translate('component.deposit.dateHeldTooltip')}
                  </TooltipContent>
                }
              />
            }
            value={formatWeekdayDate(skippedTimestamp)}
            dataTestId="deposit-date-held"
          />
        )}

        <DetailsField
          label={translate('component.deposit.site')}
          value={siteName}
          dataTestId="deposit-site"
        />

        <DetailsField
          label={translate('component.deposit.status')}
          value={<DepositStatusBadge status={status} />}
          dataTestId="deposit-status"
        />
        {!isFailedSettlement && (
          <DetailsField
            label={translate('component.deposit.reference')}
            value={shortId || '-'}
            dataTestId="deposit-reference"
          />
        )}
        {shortId && !isFailedSettlement && (
          <Flex justifyContent="end" mb="0.5rem">
            <ButtonLink
              onClick={() => {
                navigate(ROOT.PORTAL.TRANSACTIONS.path, {
                  state: {
                    depositShortId: shortId,
                  },
                })
                rvSearchPhrase(shortId ?? '')

                rvLastSelectedDates({
                  ...rvLastSelectedDates(),
                  [TIME_FILTER_ENABLED_COMPONENTS.TRANSACTIONS]: {
                    from: depositDate.subtract(2, 'day').toDate(),
                    to: depositDate.toDate(),
                  },
                })
              }}
            >
              {translate('component.deposit.viewTransactions')}
            </ButtonLink>
          </Flex>
        )}

        <Divider margin="1.5rem 0" />

        <DetailsField
          label={translate('component.deposit.collected')}
          value={currencyFormatter(collectedAmount)}
          dataTestId="deposit-collected"
        />

        <DetailsField
          label={translate('component.deposit.merchantFees')}
          value={currencyFormatter(feeAmount)}
          dataTestId="deposit-merchant-fees"
        />

        <DetailsField
          label={translate('component.deposit.refunds')}
          value={currencyFormatter(refundsAmount)}
          dataTestId="deposit-refunds"
        />

        <DetailsField
          label={
            <TooltipLabel
              label={translate('component.deposit.chargebacks')}
              tooltipContent={
                <TooltipContent>
                  {translate('component.deposit.chargebacksTooltip')}
                </TooltipContent>
              }
            />
          }
          value={currencyFormatter(chargebacksAmount)}
          dataTestId="deposit-pending"
        />

        <DetailsField
          label={translate('component.deposit.deposit')}
          value={currencyFormatter(depositAmount)}
          dataTestId="deposit-subtotal"
        />

        <DetailsField
          label={translate('component.deposit.owing')}
          value={currencyFormatter(owingAmount)}
          dataTestId="deposit-owing"
        />
      </StyledContentWrapper>
    </DepositDetailsContainer>
  )
}
